import pptxgen from 'pptxgenjs';

import { computeBreakeven, statusLine } from './breakeven';
import { calculate } from './calc';
import { computeCashflow } from './cashflow';
import { currencyByCode } from './currency';
import { applyScenario, SCENARIO_LABELS } from './scenarios';
import {
  costCategoriesFor,
  type CostCategory,
  type EventData,
  type EventMeta,
} from './types';

// Brand palette — mirrors index.css :root tokens so the deck feels on-brand.
const C = {
  bg: '0A0E14',
  bgElev: '131A24',
  bgCard: '1B2433',
  border: '2B3648',
  text: 'E6EDF3',
  textDim: '93A1B1',
  textFaint: '5B6776',
  accent: '38BDF8',
  accentStrong: '0EA5E9',
  success: '22C55E',
  danger: 'EF4444',
  warn: 'F59E0B',
};

const SLIDE_W = 13.333;
const SLIDE_H = 7.5;

type Totals = ReturnType<typeof calculate>;

export async function exportEventToPptx(
  meta: EventMeta,
  data: EventData,
): Promise<void> {
  const isMeetup = meta.event_type === 'meetup';
  const effective = isMeetup ? data : applyScenario(data);
  const totals = calculate(effective);
  const gross = data.vatMode === 'gross' ? 1 + data.vatRate : 1;
  const symbol = currencyByCode(meta.currency).symbol;

  const pres = new pptxgen();
  pres.layout = 'LAYOUT_WIDE';
  pres.title = meta.name || 'Event estimate';
  pres.company = 'estimate.events';

  buildTitleSlide(pres, meta, data, isMeetup);
  buildSummarySlide(pres, meta, data, totals, gross, symbol, isMeetup, effective);
  buildRevenueSlide(pres, totals, gross, symbol);
  buildCostsSlide(pres, meta, data, gross, symbol);
  if (!isMeetup) buildBreakevenSlide(pres, effective, meta.currency, gross, symbol);
  if (!isMeetup && meta.start_date) {
    buildCashflowSlide(pres, effective, meta.start_date, gross, symbol);
  }

  await pres.writeFile({ fileName: makeFileName(meta) });
}

// ---------- helpers --------------------------------------------------------

function fmtMoney(value: number, symbol: string): string {
  const rounded = Math.round(value);
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded).toLocaleString('en-US').replace(/,/g, ' ');
  const trailing = symbol === 'zł' || symbol === 'kr' || symbol === 'Kč';
  return trailing ? `${sign}${abs} ${symbol}` : `${sign}${symbol}${abs}`;
}

function fmtRange(
  min: number,
  max: number,
  symbol: string,
): string {
  if (Math.round(min) === Math.round(max)) return fmtMoney(min, symbol);
  return `${fmtMoney(min, symbol)} – ${fmtMoney(max, symbol)}`;
}

function netColor(value: number): string {
  return value >= 0 ? C.success : C.danger;
}

// Solid card. pptxgenjs uses inches; coordinates are absolute.
function card(
  slide: pptxgen.Slide,
  x: number,
  y: number,
  w: number,
  h: number,
  fill = C.bgCard,
) {
  slide.addShape('roundRect', {
    x,
    y,
    w,
    h,
    fill: { color: fill },
    line: { color: C.border, width: 0.75 },
    rectRadius: 0.12,
  });
}

function setDarkBackground(slide: pptxgen.Slide) {
  slide.background = { color: C.bg };
}

// ---------- chart primitives (shape-based) -------------------------------
// Native pptxgenjs charts get rejected by Keynote ("invalid format"); we
// draw charts as shape compositions so they render identically across
// PowerPoint, Keynote, Google Slides, and LibreOffice.

type ChartArea = { x: number; y: number; w: number; h: number };

function fmtCompact(value: number, symbol: string): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  let display: string;
  if (abs >= 1_000_000) display = (abs / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  else if (abs >= 1_000) display = (abs / 1_000).toFixed(0) + 'k';
  else display = Math.round(abs).toString();
  const trailing = symbol === 'zł' || symbol === 'kr' || symbol === 'Kč';
  return trailing ? `${sign}${display} ${symbol}` : `${sign}${symbol}${display}`;
}

function niceTicks(maxValue: number, targetCount = 5): number[] {
  if (maxValue <= 0) return [0];
  const rough = maxValue / targetCount;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  let step: number;
  if (norm < 1.5) step = mag;
  else if (norm < 3) step = 2 * mag;
  else if (norm < 7) step = 5 * mag;
  else step = 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= maxValue + step / 2; v += step) ticks.push(v);
  return ticks;
}

function chartBackdrop(slide: pptxgen.Slide, area: ChartArea) {
  // White panel sits behind the chart, contrasts with the dark slide.
  slide.addShape('roundRect', {
    x: area.x - 0.1,
    y: area.y - 0.1,
    w: area.w + 0.2,
    h: area.h + 0.2,
    fill: { color: 'FFFFFF' },
    line: { color: 'E5E7EB', width: 0.5 },
    rectRadius: 0.08,
  });
}

function emptyState(slide: pptxgen.Slide, area: ChartArea, text: string) {
  slide.addText(text, {
    x: area.x,
    y: area.y + area.h / 2 - 0.25,
    w: area.w,
    h: 0.5,
    fontSize: 16,
    color: '6B7280',
    align: 'center',
    fontFace: 'Calibri',
    italic: true,
  });
}

function drawGroupedColumnChart(
  slide: pptxgen.Slide,
  area: ChartArea,
  categories: string[],
  series: { name: string; color: string; values: number[] }[],
  fmt: (n: number) => string,
) {
  chartBackdrop(slide, area);

  let maxVal = 0;
  for (const s of series) for (const v of s.values) if (v > maxVal) maxVal = v;
  if (maxVal === 0) {
    emptyState(slide, area, 'No revenue planned yet');
    return;
  }

  const yLabelW = 0.85;
  const xLabelH = 0.4;
  const legendH = 0.45;
  const plotX = area.x + yLabelW;
  const plotY = area.y + 0.2;
  const plotW = area.w - yLabelW - 0.1;
  const plotH = area.h - xLabelH - legendH - 0.2;

  const ticks = niceTicks(maxVal, 5);
  const yMax = ticks[ticks.length - 1];

  // Gridlines + y labels
  for (const tick of ticks) {
    const yy = plotY + plotH - (tick / yMax) * plotH;
    slide.addText(fmt(tick), {
      x: area.x,
      y: yy - 0.12,
      w: yLabelW - 0.1,
      h: 0.24,
      fontSize: 9,
      color: '6B7280',
      align: 'right',
      fontFace: 'Calibri',
      valign: 'middle',
    });
    if (tick > 0) {
      slide.addShape('line', {
        x: plotX, y: yy, w: plotW, h: 0,
        line: { color: 'E5E7EB', width: 0.5 },
      });
    }
  }
  // Baseline
  slide.addShape('line', {
    x: plotX, y: plotY + plotH, w: plotW, h: 0,
    line: { color: '9CA3AF', width: 0.75 },
  });

  // Bars
  const groupW = plotW / categories.length;
  const groupPad = Math.min(0.4, groupW * 0.15);
  const innerW = groupW - groupPad * 2;
  const barCount = series.length;
  const barW = (innerW / barCount) * 0.78;
  const barGap =
    barCount > 1 ? (innerW - barW * barCount) / (barCount - 1) : 0;

  categories.forEach((cat, ci) => {
    const groupStartX = plotX + ci * groupW + groupPad;
    series.forEach((s, si) => {
      const v = Math.max(0, s.values[ci]);
      const barH = (v / yMax) * plotH;
      const bx = groupStartX + si * (barW + barGap);
      const by = plotY + plotH - barH;
      if (barH > 0.02) {
        slide.addShape('rect', {
          x: bx, y: by, w: barW, h: barH,
          fill: { color: s.color },
          line: { color: s.color, width: 0 },
        });
        slide.addText(fmt(v), {
          x: bx - 0.25,
          y: by - 0.28,
          w: barW + 0.5,
          h: 0.24,
          fontSize: 9,
          bold: true,
          color: '111827',
          align: 'center',
          fontFace: 'Calibri',
        });
      }
    });
    slide.addText(cat, {
      x: plotX + ci * groupW,
      y: plotY + plotH + 0.08,
      w: groupW,
      h: xLabelH - 0.05,
      fontSize: 11,
      bold: true,
      color: '111827',
      align: 'center',
      fontFace: 'Calibri',
    });
  });

  // Legend
  const legendY = area.y + area.h - legendH + 0.1;
  const itemW = 1.5;
  let legendX = area.x + area.w / 2 - (series.length * itemW) / 2;
  for (const s of series) {
    slide.addShape('rect', {
      x: legendX, y: legendY + 0.07, w: 0.22, h: 0.18,
      fill: { color: s.color },
      line: { color: s.color, width: 0 },
    });
    slide.addText(s.name, {
      x: legendX + 0.3,
      y: legendY,
      w: itemW - 0.32,
      h: 0.32,
      fontSize: 10,
      color: '374151',
      fontFace: 'Calibri',
      valign: 'middle',
    });
    legendX += itemW;
  }
}

function drawHorizontalBarChart(
  slide: pptxgen.Slide,
  area: ChartArea,
  rows: { label: string; value: number }[],
  color: string,
  fmt: (n: number) => string,
) {
  chartBackdrop(slide, area);
  if (rows.length === 0) {
    emptyState(slide, area, 'No costs recorded yet');
    return;
  }
  const maxVal = Math.max(...rows.map((r) => r.value));
  if (maxVal === 0) {
    emptyState(slide, area, 'No costs recorded yet');
    return;
  }

  const labelW = 1.5;
  const valueW = 1.2;
  const plotX = area.x + labelW;
  const plotY = area.y + 0.15;
  const plotW = area.w - labelW - valueW - 0.1;
  const plotH = area.h - 0.3;

  const rowH = plotH / rows.length;
  const barH = Math.min(0.55, rowH * 0.62);

  rows.forEach((r, i) => {
    const cy = plotY + i * rowH + rowH / 2;
    const barLen = (r.value / maxVal) * plotW;
    slide.addText(r.label, {
      x: area.x,
      y: cy - 0.2,
      w: labelW - 0.1,
      h: 0.4,
      fontSize: 12,
      bold: true,
      color: '111827',
      align: 'right',
      valign: 'middle',
      fontFace: 'Calibri',
    });
    if (barLen > 0.02) {
      slide.addShape('rect', {
        x: plotX, y: cy - barH / 2, w: barLen, h: barH,
        fill: { color },
        line: { color, width: 0 },
      });
    }
    slide.addText(fmt(r.value), {
      x: plotX + barLen + 0.08,
      y: cy - 0.2,
      w: valueW,
      h: 0.4,
      fontSize: 11,
      bold: true,
      color: '111827',
      align: 'left',
      valign: 'middle',
      fontFace: 'Calibri',
    });
  });
}

function drawColumnTimelineChart(
  slide: pptxgen.Slide,
  area: ChartArea,
  bars: { label: string; value: number }[],
  posColor: string,
  negColor: string,
  fmt: (n: number) => string,
) {
  chartBackdrop(slide, area);
  if (bars.length === 0) {
    emptyState(slide, area, 'No cashflow movements');
    return;
  }

  const yLabelW = 0.85;
  const xLabelH = 0.5;
  const plotX = area.x + yLabelW;
  const plotY = area.y + 0.2;
  const plotW = area.w - yLabelW - 0.1;
  const plotH = area.h - xLabelH - 0.3;

  let minVal = 0;
  let maxVal = 0;
  for (const b of bars) {
    if (b.value < minVal) minVal = b.value;
    if (b.value > maxVal) maxVal = b.value;
  }
  if (minVal === 0 && maxVal === 0) {
    emptyState(slide, area, 'No cashflow movements');
    return;
  }
  // Pad the range slightly so bars don't touch the chart edges.
  const span = (maxVal - minVal) || 1;
  const pad = span * 0.05;
  const yLo = minVal - pad;
  const yHi = maxVal + pad;
  const yRange = yHi - yLo;

  const yToPx = (v: number) => plotY + plotH - ((v - yLo) / yRange) * plotH;
  const zeroY = yToPx(0);

  // Gridlines
  const posTicks = maxVal > 0 ? niceTicks(maxVal, 3) : [];
  const negTicks =
    minVal < 0
      ? niceTicks(-minVal, 3).map((t) => -t).filter((t) => t < 0).reverse()
      : [];
  const ticks = [...negTicks, 0, ...posTicks.filter((t) => t > 0)];

  for (const tick of ticks) {
    const yy = yToPx(tick);
    if (yy < plotY - 0.01 || yy > plotY + plotH + 0.01) continue;
    slide.addText(fmt(tick), {
      x: area.x,
      y: yy - 0.12,
      w: yLabelW - 0.1,
      h: 0.24,
      fontSize: 8,
      color: '6B7280',
      align: 'right',
      valign: 'middle',
      fontFace: 'Calibri',
    });
    slide.addShape('line', {
      x: plotX, y: yy, w: plotW, h: 0,
      line: {
        color: tick === 0 ? '9CA3AF' : 'E5E7EB',
        width: tick === 0 ? 0.75 : 0.5,
      },
    });
  }

  // Bars
  const barSlot = plotW / bars.length;
  const barW = Math.max(0.05, barSlot * 0.72);
  const barOffset = (barSlot - barW) / 2;

  bars.forEach((b, i) => {
    const bx = plotX + i * barSlot + barOffset;
    const yBar = yToPx(b.value);
    if (b.value >= 0) {
      const h = zeroY - yBar;
      if (h > 0.02) {
        slide.addShape('rect', {
          x: bx, y: yBar, w: barW, h,
          fill: { color: posColor },
          line: { color: posColor, width: 0 },
        });
      }
    } else {
      const h = yBar - zeroY;
      if (h > 0.02) {
        slide.addShape('rect', {
          x: bx, y: zeroY, w: barW, h,
          fill: { color: negColor },
          line: { color: negColor, width: 0 },
        });
      }
    }
  });

  // X labels — sample to fit at most ~8 labels.
  const maxLabels = 8;
  const step = Math.max(1, Math.ceil(bars.length / maxLabels));
  bars.forEach((b, i) => {
    const isLast = i === bars.length - 1;
    if (i % step !== 0 && !isLast) return;
    const bx = plotX + i * barSlot + barOffset + barW / 2;
    slide.addText(b.label, {
      x: bx - barSlot * 1.5,
      y: plotY + plotH + 0.08,
      w: barSlot * 3,
      h: 0.35,
      fontSize: 8,
      color: '374151',
      align: 'center',
      fontFace: 'Calibri',
    });
  });
}

function pageHeader(
  slide: pptxgen.Slide,
  meta: EventMeta,
  title: string,
  kicker?: string,
) {
  // Top accent bar.
  slide.addShape('rect', {
    x: 0,
    y: 0,
    w: SLIDE_W,
    h: 0.08,
    fill: { color: C.accent },
    line: { color: C.accent },
  });
  if (kicker) {
    slide.addText(kicker.toUpperCase(), {
      x: 0.5,
      y: 0.25,
      w: 8,
      h: 0.3,
      fontFace: 'Calibri',
      fontSize: 11,
      bold: true,
      color: C.accent,
      charSpacing: 4,
    });
  }
  slide.addText(title, {
    x: 0.5,
    y: kicker ? 0.55 : 0.4,
    w: 12,
    h: 0.65,
    fontFace: 'Calibri',
    fontSize: 28,
    bold: true,
    color: C.text,
  });
  slide.addText(meta.name || '(untitled)', {
    x: 0.5,
    y: SLIDE_H - 0.45,
    w: 12.3,
    h: 0.3,
    fontFace: 'Calibri',
    fontSize: 10,
    color: C.textFaint,
    align: 'left',
  });
}

// ---------- slide 1: title -------------------------------------------------

function buildTitleSlide(
  pres: pptxgen,
  meta: EventMeta,
  data: EventData,
  isMeetup: boolean,
) {
  const slide = pres.addSlide();
  setDarkBackground(slide);

  // Decorative band
  slide.addShape('rect', {
    x: 0,
    y: 0,
    w: SLIDE_W,
    h: 0.5,
    fill: { color: C.accent },
    line: { color: C.accent },
  });
  slide.addShape('rect', {
    x: 0,
    y: SLIDE_H - 0.5,
    w: SLIDE_W,
    h: 0.5,
    fill: { color: C.bgElev },
    line: { color: C.bgElev },
  });

  slide.addText('REVENUE ESTIMATE', {
    x: 0.8,
    y: 1.5,
    w: 11.5,
    h: 0.5,
    fontFace: 'Calibri',
    fontSize: 18,
    bold: true,
    color: C.accent,
    charSpacing: 8,
  });
  slide.addText(meta.name || '(untitled)', {
    x: 0.8,
    y: 2.1,
    w: 11.5,
    h: 1.5,
    fontFace: 'Calibri',
    fontSize: 54,
    bold: true,
    color: C.text,
  });

  const dateLine = [meta.start_date, meta.end_date]
    .filter(Boolean)
    .join(' – ');
  const locLine = [meta.city, meta.country_code].filter(Boolean).join(', ');
  const typeLine = isMeetup ? 'Meetup' : 'Conference';
  const sub = [typeLine, dateLine, locLine].filter(Boolean).join('  ·  ');
  slide.addText(sub, {
    x: 0.8,
    y: 3.8,
    w: 11.5,
    h: 0.5,
    fontFace: 'Calibri',
    fontSize: 22,
    color: C.textDim,
  });

  // Footer band: scenario + VAT + currency
  const scenario = isMeetup ? null : SCENARIO_LABELS[data.scenario.active];
  const vatLine =
    data.vatMode === 'gross'
      ? `Gross (+${(data.vatRate * 100).toFixed(1)}% VAT)`
      : 'Net of VAT';
  const footerBits = [meta.currency, vatLine];
  if (scenario) footerBits.push(`Scenario: ${scenario}`);
  slide.addText(footerBits.join('  ·  '), {
    x: 0.8,
    y: SLIDE_H - 0.45,
    w: 8,
    h: 0.3,
    fontFace: 'Calibri',
    fontSize: 11,
    color: C.textFaint,
  });
  slide.addText('estimate.events', {
    x: SLIDE_W - 3.3,
    y: SLIDE_H - 0.45,
    w: 3,
    h: 0.3,
    fontFace: 'Calibri',
    fontSize: 11,
    color: C.textFaint,
    align: 'right',
  });
}

// ---------- slide 2: executive summary ------------------------------------

function buildSummarySlide(
  pres: pptxgen,
  meta: EventMeta,
  data: EventData,
  totals: Totals,
  gross: number,
  symbol: string,
  isMeetup: boolean,
  effective: EventData,
) {
  const slide = pres.addSlide();
  setDarkBackground(slide);
  pageHeader(slide, meta, 'Executive summary', 'At a glance');

  // Headline NET card
  const netMin = totals.net.min * gross;
  const netMax = totals.net.max * gross;
  card(slide, 0.5, 1.4, 12.3, 1.8);
  slide.addText('Net (revenue − costs)', {
    x: 0.8,
    y: 1.55,
    w: 11,
    h: 0.4,
    fontFace: 'Calibri',
    fontSize: 13,
    color: C.textDim,
  });
  const netDisplay = fmtRange(netMin, netMax, symbol);
  slide.addText(netDisplay, {
    x: 0.8,
    y: 1.95,
    w: 11.7,
    h: 1.0,
    fontFace: 'Calibri',
    fontSize: 44,
    bold: true,
    color: netColor((netMin + netMax) / 2),
  });

  // Status line (break-even) under headline
  if (!isMeetup) {
    const be = computeBreakeven(effective);
    const status = statusLine(be, meta.currency);
    if (status) {
      const toneColor =
        status.tone === 'good' ? C.success :
        status.tone === 'warn' ? C.warn :
        status.tone === 'bad' ? C.danger : C.textDim;
      slide.addText(status.text, {
        x: 0.8,
        y: 2.8,
        w: 11.7,
        h: 0.35,
        fontFace: 'Calibri',
        fontSize: 12,
        bold: true,
        color: toneColor,
      });
    }
  }

  // KPI tiles row
  const kpis: { label: string; value: string; color?: string }[] = [
    {
      label: 'Revenue',
      value: fmtRange(totals.revenue.min * gross, totals.revenue.max * gross, symbol),
      color: C.accent,
    },
    {
      label: 'Costs',
      value: fmtMoney(totals.costs * gross, symbol),
      color: C.danger,
    },
    { label: 'Tickets', value: totals.ticketCount.toLocaleString('en-US') },
    { label: 'Sponsors', value: totals.sponsorCount.toLocaleString('en-US') },
  ];

  const tileW = 2.95;
  const tileH = 1.7;
  const gap = 0.15;
  const startX = 0.5;
  const tileY = 3.5;
  kpis.forEach((k, i) => {
    const x = startX + i * (tileW + gap);
    card(slide, x, tileY, tileW, tileH);
    slide.addText(k.label.toUpperCase(), {
      x: x + 0.2,
      y: tileY + 0.15,
      w: tileW - 0.4,
      h: 0.3,
      fontFace: 'Calibri',
      fontSize: 10,
      bold: true,
      color: C.textDim,
      charSpacing: 3,
    });
    slide.addText(k.value, {
      x: x + 0.2,
      y: tileY + 0.55,
      w: tileW - 0.4,
      h: 1.05,
      fontFace: 'Calibri',
      fontSize: 20,
      bold: true,
      color: k.color ?? C.text,
      valign: 'middle',
    });
  });

  // Per-attendee row
  if (totals.ticketCount > 0) {
    const perRev = (totals.perAttendee.revenue.min + totals.perAttendee.revenue.max) / 2 * gross;
    const perCost = totals.perAttendee.cost * gross;
    card(slide, 0.5, 5.4, 12.3, 1.4);
    slide.addText('Per attendee', {
      x: 0.8,
      y: 5.5,
      w: 11,
      h: 0.3,
      fontFace: 'Calibri',
      fontSize: 11,
      color: C.textDim,
    });
    slide.addText(
      [
        { text: 'Revenue ', options: { color: C.textDim, fontSize: 14 } },
        {
          text: fmtMoney(perRev, symbol),
          options: { color: C.accent, fontSize: 22, bold: true },
        },
        { text: '     Cost ', options: { color: C.textDim, fontSize: 14 } },
        {
          text: fmtMoney(perCost, symbol),
          options: { color: C.danger, fontSize: 22, bold: true },
        },
        { text: '     Margin ', options: { color: C.textDim, fontSize: 14 } },
        {
          text: fmtMoney(perRev - perCost, symbol),
          options: {
            color: netColor(perRev - perCost),
            fontSize: 22,
            bold: true,
          },
        },
      ],
      {
        x: 0.8,
        y: 5.85,
        w: 11.7,
        h: 0.75,
        fontFace: 'Calibri',
        valign: 'middle',
      },
    );
  }
}

// ---------- slide 3: revenue breakdown ------------------------------------

function buildRevenueSlide(
  pres: pptxgen,
  totals: Totals,
  gross: number,
  symbol: string,
) {
  const slide = pres.addSlide();
  setDarkBackground(slide);
  pageHeader(slide, { name: '' } as EventMeta, 'Revenue breakdown', 'Where the money comes from');

  // Grouped bar chart (Tickets / Sponsors / Add-ons × Min / Max)
  const labels = ['Tickets', 'Sponsors', 'Add-ons'];
  const chartData = [
    {
      name: 'Min price',
      labels,
      values: [
        totals.ticketRevenue.min * gross,
        totals.sponsorRevenue.min * gross,
        totals.addonRevenue * gross,
      ],
    },
    {
      name: 'Max price',
      labels,
      values: [
        totals.ticketRevenue.max * gross,
        totals.sponsorRevenue.max * gross,
        totals.addonRevenue * gross,
      ],
    },
  ];

  drawGroupedColumnChart(
    slide,
    { x: 0.6, y: 1.5, w: 8.5, h: 5.4 },
    chartData[0].labels,
    [
      { name: 'Min price', color: C.accent, values: chartData[0].values },
      { name: 'Max price', color: C.accentStrong, values: chartData[1].values },
    ],
    (v) => fmtCompact(v, symbol),
  );

  // Right-hand totals stack
  const totalMin = totals.revenue.min * gross;
  const totalMax = totals.revenue.max * gross;
  card(slide, 9.5, 1.5, 3.3, 2.4);
  slide.addText('TOTAL REVENUE', {
    x: 9.7, y: 1.65, w: 3, h: 0.3,
    fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
  });
  slide.addText(fmtMoney(totalMin, symbol), {
    x: 9.7, y: 2.0, w: 3, h: 0.6,
    fontFace: 'Calibri', fontSize: 22, bold: true, color: C.accent,
  });
  if (Math.round(totalMin) !== Math.round(totalMax)) {
    slide.addText(`to  ${fmtMoney(totalMax, symbol)}`, {
      x: 9.7, y: 2.65, w: 3, h: 0.5,
      fontFace: 'Calibri', fontSize: 14, color: C.accentStrong,
    });
  }

  card(slide, 9.5, 4.0, 3.3, 2.9);
  slide.addText('COUNTS', {
    x: 9.7, y: 4.15, w: 3, h: 0.3,
    fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
  });
  const countLines: pptxgen.TextProps[] = [
    { text: 'Tickets', options: { color: C.textDim, fontSize: 12 } },
    { text: '   ' + totals.ticketCount.toLocaleString('en-US') + '\n',
      options: { color: C.text, fontSize: 16, bold: true } },
    { text: 'Sponsors', options: { color: C.textDim, fontSize: 12 } },
    { text: '   ' + totals.sponsorCount.toLocaleString('en-US') + '\n',
      options: { color: C.text, fontSize: 16, bold: true } },
    { text: 'Add-ons enabled', options: { color: C.textDim, fontSize: 12 } },
    { text: '   ' + totals.addonCount.toLocaleString('en-US'),
      options: { color: C.text, fontSize: 16, bold: true } },
  ];
  slide.addText(countLines, {
    x: 9.7, y: 4.5, w: 3, h: 2.3,
    fontFace: 'Calibri', valign: 'top',
  });
}

// ---------- slide 4: costs breakdown --------------------------------------

function buildCostsSlide(
  pres: pptxgen,
  meta: EventMeta,
  data: EventData,
  gross: number,
  symbol: string,
) {
  const slide = pres.addSlide();
  setDarkBackground(slide);
  pageHeader(slide, meta, 'Cost breakdown', 'Where the money goes');

  const cats = costCategoriesFor(meta.event_type);
  const rows = cats
    .map((c) => ({
      label: c.label,
      total:
        (data.costs[c.key as CostCategory] ?? []).reduce(
          (acc, i) => acc + i.amount,
          0,
        ) * gross,
    }))
    .filter((r) => r.total > 0);

  const grand = rows.reduce((acc, r) => acc + r.total, 0);

  if (rows.length === 0) {
    slide.addText('No costs recorded yet.', {
      x: 0.6, y: 3.0, w: 12.2, h: 0.7,
      fontFace: 'Calibri', fontSize: 22, color: C.textDim, align: 'center',
    });
    return;
  }

  const sorted = [...rows].sort((a, b) => b.total - a.total);
  drawHorizontalBarChart(
    slide,
    { x: 0.6, y: 1.5, w: 8.5, h: 5.4 },
    sorted.map((r) => ({ label: r.label, value: r.total })),
    C.danger,
    (v) => fmtCompact(v, symbol),
  );

  // Totals card
  card(slide, 9.5, 1.5, 3.3, 2.0);
  slide.addText('TOTAL COSTS', {
    x: 9.7, y: 1.65, w: 3, h: 0.3,
    fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
  });
  slide.addText(fmtMoney(grand, symbol), {
    x: 9.7, y: 2.0, w: 3, h: 1.2,
    fontFace: 'Calibri', fontSize: 26, bold: true, color: C.danger,
  });

  // Top 3 categories
  card(slide, 9.5, 3.6, 3.3, 3.3);
  slide.addText('TOP CATEGORIES', {
    x: 9.7, y: 3.75, w: 3, h: 0.3,
    fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
  });
  const top = sorted.slice(0, 5);
  const topText: pptxgen.TextProps[] = [];
  for (const r of top) {
    const pct = grand > 0 ? (r.total / grand) * 100 : 0;
    topText.push({
      text: r.label + '\n',
      options: { color: C.text, fontSize: 13, bold: true },
    });
    topText.push({
      text: `${fmtMoney(r.total, symbol)}  ·  ${pct.toFixed(0)}%\n\n`,
      options: { color: C.textDim, fontSize: 11 },
    });
  }
  slide.addText(topText, {
    x: 9.7, y: 4.1, w: 3, h: 2.7,
    fontFace: 'Calibri', valign: 'top',
  });
}

// ---------- slide 5: break-even -------------------------------------------

function buildBreakevenSlide(
  pres: pptxgen,
  effective: EventData,
  currency: string,
  gross: number,
  symbol: string,
) {
  const slide = pres.addSlide();
  setDarkBackground(slide);
  pageHeader(slide, { name: '' } as EventMeta, 'Break-even analysis', 'Can this event pay for itself?');

  const be = computeBreakeven(effective);
  const status = statusLine(be, currency);

  if (status) {
    const toneColor =
      status.tone === 'good' ? C.success :
      status.tone === 'warn' ? C.warn :
      status.tone === 'bad' ? C.danger : C.textDim;
    card(slide, 0.5, 1.4, 12.3, 0.85, C.bgCard);
    slide.addText(status.text, {
      x: 0.8, y: 1.5, w: 11.7, h: 0.65,
      fontFace: 'Calibri', fontSize: 18, bold: true, color: toneColor,
      valign: 'middle',
    });
  }

  // Two scenario cards: at MIN prices, at MAX prices
  const scenarioCard = (
    x: number,
    title: string,
    s: typeof be.min,
  ) => {
    card(slide, x, 2.5, 6.05, 4.4);
    slide.addText(title, {
      x: x + 0.25, y: 2.65, w: 5.5, h: 0.4,
      fontFace: 'Calibri', fontSize: 14, bold: true, color: C.accent,
    });
    const verdict = s.profitable ? 'Profitable' : 'Below break-even';
    slide.addText(verdict, {
      x: x + 0.25, y: 3.05, w: 5.5, h: 0.45,
      fontFace: 'Calibri', fontSize: 20, bold: true,
      color: s.profitable ? C.success : C.danger,
    });
    slide.addText(
      s.profitable
        ? `Headroom: ${fmtMoney(-s.gap * gross, symbol)}`
        : `Gap: ${fmtMoney(s.gap * gross, symbol)}`,
      {
        x: x + 0.25, y: 3.5, w: 5.5, h: 0.35,
        fontFace: 'Calibri', fontSize: 12, color: C.textDim,
      },
    );

    // Levers (only shown when not profitable)
    const levers: pptxgen.TextProps[] = [];
    if (!s.profitable) {
      levers.push({
        text: 'To break even, choose one:\n',
        options: { color: C.textDim, fontSize: 11, italic: true },
      });
      if (s.tickets.computable && s.tickets.needed > 0) {
        const pct = Number.isFinite(s.tickets.pctOfPlan)
          ? `  (${Math.round(s.tickets.pctOfPlan * 100)}% of plan)`
          : '';
        levers.push({
          text: '• Sell ',
          options: { color: C.text, fontSize: 13 },
        });
        levers.push({
          text: s.tickets.needed.toLocaleString('en-US') + ' tickets',
          options: { color: C.text, fontSize: 13, bold: true },
        });
        levers.push({
          text: pct + '\n',
          options: { color: C.textDim, fontSize: 12 },
        });
      }
      if (s.sponsors.computable && s.sponsors.needed > 0) {
        const pct = Number.isFinite(s.sponsors.pctOfPlan)
          ? `  (${Math.round(s.sponsors.pctOfPlan * 100)}% of plan)`
          : '';
        levers.push({
          text: '• Land ',
          options: { color: C.text, fontSize: 13 },
        });
        levers.push({
          text: s.sponsors.needed.toLocaleString('en-US') + ' sponsors',
          options: { color: C.text, fontSize: 13, bold: true },
        });
        levers.push({
          text: pct + '\n',
          options: { color: C.textDim, fontSize: 12 },
        });
      }
      levers.push({
        text: '• Cut ',
        options: { color: C.text, fontSize: 13 },
      });
      levers.push({
        text: fmtMoney(s.costDown.needed * gross, symbol),
        options: { color: C.text, fontSize: 13, bold: true },
      });
      levers.push({
        text: ' in costs',
        options: { color: C.textDim, fontSize: 12 },
      });
    }
    if (levers.length > 0) {
      slide.addText(levers, {
        x: x + 0.25, y: 4.0, w: 5.55, h: 2.5,
        fontFace: 'Calibri', valign: 'top',
        paraSpaceAfter: 4,
      });
    }
  };

  scenarioCard(0.5, 'At MIN prices', be.min);
  scenarioCard(6.75, 'At MAX prices', be.max);
}

// ---------- slide 6: cashflow ---------------------------------------------

function buildCashflowSlide(
  pres: pptxgen,
  effective: EventData,
  startDate: string,
  gross: number,
  symbol: string,
) {
  const cf = computeCashflow(effective, startDate);
  if (cf.weeks.length === 0) return;

  const slide = pres.addSlide();
  setDarkBackground(slide);
  pageHeader(slide, { name: '' } as EventMeta, 'Cashflow timeline', 'When the money actually moves');

  drawColumnTimelineChart(
    slide,
    { x: 0.6, y: 1.5, w: 8.5, h: 5.4 },
    cf.weeks.map((w) => ({
      // Compact MM-DD label — full ISO would crowd the axis.
      label: w.weekStart.slice(5),
      value: w.cumulative * gross,
    })),
    C.success,
    C.danger,
    (v) => fmtCompact(v, symbol),
  );

  // Right-hand stats cards
  card(slide, 9.5, 1.5, 3.3, 1.5);
  slide.addText('FINAL NET', {
    x: 9.7, y: 1.62, w: 3, h: 0.3,
    fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
  });
  slide.addText(fmtMoney(cf.finalNet * gross, symbol), {
    x: 9.7, y: 1.95, w: 3, h: 0.85,
    fontFace: 'Calibri', fontSize: 22, bold: true, color: netColor(cf.finalNet),
  });

  if (cf.trough) {
    card(slide, 9.5, 3.1, 3.3, 1.85);
    slide.addText('TROUGH (LOWEST POINT)', {
      x: 9.7, y: 3.22, w: 3, h: 0.3,
      fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
    });
    slide.addText(fmtMoney(cf.trough.amount * gross, symbol), {
      x: 9.7, y: 3.55, w: 3, h: 0.6,
      fontFace: 'Calibri', fontSize: 18, bold: true,
      color: cf.trough.amount < 0 ? C.danger : C.text,
    });
    const tDay = cf.trough.daysToEvent;
    const tDayLabel = tDay === 0 ? 'event week' : tDay > 0 ? `T−${tDay}d` : `T+${-tDay}d`;
    slide.addText(`${cf.trough.date}  ·  ${tDayLabel}`, {
      x: 9.7, y: 4.2, w: 3, h: 0.5,
      fontFace: 'Calibri', fontSize: 11, color: C.textDim,
    });
  }

  card(slide, 9.5, 5.05, 3.3, 1.85);
  slide.addText('FLOWS', {
    x: 9.7, y: 5.17, w: 3, h: 0.3,
    fontFace: 'Calibri', fontSize: 10, bold: true, color: C.textDim, charSpacing: 3,
  });
  slide.addText(
    [
      { text: 'Inflow\n', options: { color: C.textDim, fontSize: 11 } },
      {
        text: fmtMoney(cf.totalIn * gross, symbol) + '\n',
        options: { color: C.success, fontSize: 15, bold: true },
      },
      { text: 'Outflow\n', options: { color: C.textDim, fontSize: 11 } },
      {
        text: fmtMoney(cf.totalOut * gross, symbol),
        options: { color: C.danger, fontSize: 15, bold: true },
      },
    ],
    {
      x: 9.7, y: 5.5, w: 3, h: 1.35,
      fontFace: 'Calibri', valign: 'top',
    },
  );
}

// ---------- filename ------------------------------------------------------

function makeFileName(meta: EventMeta): string {
  const safe = (meta.name || 'event')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const date = meta.start_date ? `_${meta.start_date}` : '';
  return `${safe || 'event'}${date}.pptx`;
}
